import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Verified } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";

import {
  CATEGORY_STATUS_IDS,
  EXPORTER_STATUS,
  generalImporterPagesPadding
} from "../../../../utils/Constants";
import {
  GreyBriefCaseIcon,
  GreyCalendarIcon,
  GreyConvertCubeIcon,
  GreyEnvelopeIcon,
  GreyGlobeIcon,
  GreyLocationIcon,
  GreyProfileIcon,
  GreyWhatsappIcon
} from "../../../../utils/Icons";
import noImgPlaceholder from "../../../../assets/images/empty_data.png";
import noCoverImgPlaceholder from "../../../../assets/images/exporter-profile-cover.png";
import messages from "../../../../assets/locale/messages";
import EmptyState from "../../../../components/EmptyState";
import noShowcasesImage from "../../../../assets/images/placeholders/noShowcasePlaceholder.svg";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  checkBackgroundImageValidity,
  checkForMultiline
} from "../../../../utils/Helpers";
import ProductCardGrid from "../../../../components/ProductCards/ProductCardGrid";
import Tabs from "../../../../components/Tabs";
import VerificationAndSubscription from "./VerificationAndSubscription";
import {
  getCompanyContactInfoRequest,
  getCompanyContactInfoResponse,
  getExporterAccountInfoRequest,
  getExporterAccountInfoResponse,
  getExporterCompanyDetailsRequest,
  getExporterCompanyDetailsResponse
} from "../../../../store/Users/actions";
import TruncateText from "../../../../components/TruncateText";
import "./ExporterDetails.scss";

const ExporterDetails = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { companyId, id } = useParams();
  const exporterCompanyDetails = useSelector(
    (state) => state.users.exporterDetails.exporterCompanyDetails
  );
  const companyContactInfo = useSelector(
    (state) => state.users.exporterDetails.companyContactInfo
  );
  const exporterAccountInfo = useSelector(
    (state) => state.users.exporterDetails.exporterAccountInfo
  );
  useEffect(() => {
    return () => {
      dispatch(getExporterCompanyDetailsResponse({}));
      dispatch(getCompanyContactInfoResponse({}));
      dispatch(getExporterAccountInfoResponse({}));
    };
  }, []);

  useEffect(() => {
    if (companyId) {
      dispatch(getCompanyContactInfoRequest({ id: companyId }));
      dispatch(
        getExporterCompanyDetailsRequest({
          params: {
            id: companyId
          }
        })
      );
    }
    if (id) {
      dispatch(getExporterAccountInfoRequest({ id }));
    }
  }, [companyId, id]);

  const {
    users: {
      exporterDetails: {
        pageTitle,
        tabs: { companyProfile, accountInfo, verificationAndPricing },
        mainInfo: {
          description,
          categories,
          companyInfo,
          yearEstablished,
          yearsOfExperience,
          country,
          businessType
        },
        contactDetails: {
          contactInfo,
          exporterName,
          mobileNumber,
          emailAddress,
          website
        },
        showcases,
        noShowcasesEmptyPlaceholder,
        inactive,
        pendingCategoryWarning,
        rejectedCategoryWarning
      }
    }
  } = messages[lang];
  const [isCompanyCoverImgValid, setIsCompanyCoverImgValid] =
    useState(true);
  const [isCompanyLogoImgValid, setIsCompanyLogoImgValid] =
    useState(true);
  const [validShowcaseImagesId, setValidShowcaseImagesId] = useState(
    []
  );
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    checkBackgroundImageValidity(
      exporterCompanyDetails?.LogoImage,
      (isLoaded) => {
        setIsCompanyLogoImgValid(isLoaded);
      }
    );
    checkBackgroundImageValidity(
      exporterCompanyDetails?.CoverImage,
      (isLoaded) => {
        setIsCompanyCoverImgValid(isLoaded);
      }
    );
  }, [
    exporterCompanyDetails,
    exporterCompanyDetails?.LogoImage,
    exporterCompanyDetails?.CoverImage
  ]);

  useEffect(() => {
    if (
      exporterCompanyDetails &&
      exporterCompanyDetails.ShowCases &&
      exporterCompanyDetails?.ShowCases?.length
    ) {
      exporterCompanyDetails?.ShowCases.forEach((showcase) => {
        checkBackgroundImageValidity(showcase?.Image, (isLoaded) => {
          if (isLoaded) {
            setValidShowcaseImagesId((previousState) => {
              if (previousState.includes(showcase.Id)) {
                return previousState;
              } else return [...previousState, showcase.Id];
            });
          }
        });
      });
    }
  }, [exporterCompanyDetails, validShowcaseImagesId]);

  const handleChipClass = (exporterStatusId, isActive) => {
    let chipClass;
    if (!isActive) {
      chipClass = "red-chip";
    } else {
      switch (exporterStatusId) {
        case EXPORTER_STATUS.subscribed:
          chipClass = "green-chip";
          break;
        case EXPORTER_STATUS.verified:
          chipClass = "blue-chip";
          break;
        case EXPORTER_STATUS.businessVerified:
          chipClass = "blue-chip";
          break;
        case EXPORTER_STATUS.registered:
          chipClass = "yellow-chip";
          break;
        case EXPORTER_STATUS.expired:
          chipClass = "light-brown-chip";
          break;
        default:
          chipClass = "";
      }
    }
    return chipClass;
  };

  const fillInProductCards = (productsArr) => {
    const tempProductsCardsArray = [];
    if (productsArr?.length <= 6) {
      let emptyProductsIndex = 0;
      for (let index = 0; index < 6; index++) {
        if (!!productsArr.length && productsArr[index]) {
          const {
            Id,
            Name,
            DefaultImage,
            MinOrderQuantity,
            MoqUnitName,
            MinPriceRange,
            MaxPriceRange
          } = productsArr[index];
          tempProductsCardsArray.push(
            <div
              key={`${Name}-${Id}`}
              className="single-product filled-product border-radius-10">
              <ProductCardGrid
                cardClassName="w-100"
                label={Name}
                src={DefaultImage}
                count={MinOrderQuantity}
                unit={MoqUnitName}
                minPrice={MinPriceRange}
                maxPrice={MaxPriceRange}
                link={ROUTE_PATHS.productDetailsApproved.replace(
                  ":id",
                  Id
                )}
              />
            </div>
          );
        } else {
          tempProductsCardsArray.push(
            <div
              key={`empty-card + ${emptyProductsIndex}`}
              className="single-product border-radius-10"></div>
          );
          emptyProductsIndex += 1;
        }
      }
    }
    return tempProductsCardsArray;
  };

  const renderHeaderSection = () => (
    <div
      className={`header-section ${generalImporterPagesPadding} size-cover`}
      style={{
        backgroundImage: `url(${isCompanyCoverImgValid ? exporterCompanyDetails?.CoverImage : noCoverImgPlaceholder})`
      }}>
      <div
        className="profile-img circular-border-radius white-bg light-border"
        style={{
          backgroundImage: `url(${isCompanyLogoImgValid ? exporterCompanyDetails?.LogoImage : noImgPlaceholder})`
        }}></div>
    </div>
  );

  const [truncateLength, setTruncateLength] = useState(0);
  const handleResize = () => {
    const width = window.innerWidth;

    if (width >= 768) {
      setTruncateLength(100);
    } else if (width >= 425) {
      setTruncateLength(35);
    } else if (width >= 375) {
      setTruncateLength(28);
    } else {
      setTruncateLength(19);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const renderLabelValue = (label, value, icon) => (
    <div className="d-flex align-items-center text-break">
      <div>{icon}</div>
      <div className="ms-3">
        <p className="text-light-grey mb-1 fsize-12">{label}</p>
        <div className="text-color fsize-15 fweight-500 mb-0">
          <TruncateText text={value} length={truncateLength} />
        </div>
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: companyProfile
    },
    {
      label: accountInfo
    },
    {
      label: verificationAndPricing
    }
  ];

  const nonVerifiedStatuses = [
    EXPORTER_STATUS.registered,
    EXPORTER_STATUS.verified,
    EXPORTER_STATUS.pendingBusinessVerified
  ];

  const renderExporterInfoAndTabsSection = () => (
    <div
      className={`exporter-info-section mb-4 row ${generalImporterPagesPadding}`}>
      <div className="col-12">
        <div className="tradeName-section d-flex mb-4 flex-column flex-md-row align-items-start align-items-md-center">
          <h3
            className={`fsize-24 fweight-700 title-color pe-3 mb-3 `}>
            {exporterCompanyDetails?.TradeName || "--"}
            {!nonVerifiedStatuses.includes(
              companyContactInfo.UserStatusId
            ) && <Verified color="primary" className="ms-1" />}
          </h3>
          <Box
            sx={{
              width: "2px",
              height: "35px",
              backgroundColor: "divider",
              display: {
                xs: "none",
                sm: "block",
                md: "block"
              }
            }}
          />
          {companyContactInfo &&
            companyContactInfo.UserStatusName && (
              <div className="ms-md-3 ms-0">
                <Chip
                  label={
                    companyContactInfo.IsActive
                      ? companyContactInfo.UserStatusName
                      : inactive
                  }
                  className={` border-radius-8 fw-bold ${handleChipClass(companyContactInfo.UserStatusId, companyContactInfo.IsActive)}`}
                  sx={{ fontWeight: "semibold" }}
                />
              </div>
            )}
        </div>
      </div>
      <Tabs
        className={"exporter-details-tabs"}
        tabs={tabsArray}
        value={tabValue}
        handleChange={setTabValue}
      />
    </div>
  );

  const renderAccountInfoSection = () => (
    <div
      className={`description-contact-container d-md-flex py-3 col-12`}>
      <div className="row w-100">
        <div className="col-md-6 col-12 mb-4">
          {renderLabelValue(
            exporterName,
            exporterAccountInfo?.Name || "--",
            <GreyProfileIcon dimensions={"25"} />
          )}
        </div>
        <div className="col-md-6 col-12 mb-4">
          {renderLabelValue(
            mobileNumber,
            exporterAccountInfo?.MobileNumber || "--",
            <GreyWhatsappIcon dimensions="25" />
          )}
        </div>
        <div className="col-md-6 col-12 mb-4 mb-xl-0">
          {renderLabelValue(
            emailAddress,
            exporterAccountInfo?.Email || "--",
            <GreyEnvelopeIcon dimensions="25" />
          )}
        </div>
      </div>
    </div>
  );

  const handleCategoriesChipClass = (categoryStatusId) => {
    let chipClass;
    switch (categoryStatusId) {
      case CATEGORY_STATUS_IDS.approved: {
        chipClass = "blue-chip";
        break;
      }
      case CATEGORY_STATUS_IDS.pending: {
        chipClass = "orange-chip";
        break;
      }
      case CATEGORY_STATUS_IDS.rejected: {
        chipClass = "red-chip";
        break;
      }
      default:
        chipClass = "";
    }
    return chipClass;
  };

  const renderDescriptionContactInfoSection = () => (
    <div
      className={`description-contact-container d-md-flex justify-content-md-between py-3 col-12`}>
      <div className="col-md-6 col-12">
        <h4 className="fsize-16 fweight-600 title-color mb-3">
          {description}
        </h4>
        {exporterCompanyDetails?.Introduction || "--"}
        <div className="categories-section d- mb-4 mb-md-0 py-3">
          <h4 className="fsize-16 fweight-600 title-color mb-3">
            {categories}
          </h4>
          <div className="categories-chips d-flex justify-content-start flex-wrap text-break pe-md-3 pe-0">
            {exporterCompanyDetails?.Categories &&
              !!exporterCompanyDetails.Categories.length &&
              exporterCompanyDetails?.Categories?.some(
                (category) =>
                  category.Status === CATEGORY_STATUS_IDS.pending
              ) && (
                <Typography className="text-secondary fsize-13 ps-md-1 pt-2 text-break mb-1">
                  {pendingCategoryWarning}
                </Typography>
              )}
            {exporterCompanyDetails?.Categories &&
              !!exporterCompanyDetails.Categories.length &&
              exporterCompanyDetails?.Categories?.some(
                (category) =>
                  category.Status === CATEGORY_STATUS_IDS.rejected
              ) && (
                <Typography className="text-secondary fsize-13 ps-md-1 pt-2 text-break mb-1">
                  {rejectedCategoryWarning}
                </Typography>
              )}
            {exporterCompanyDetails?.Categories &&
            !!exporterCompanyDetails.Categories.length
              ? exporterCompanyDetails?.Categories.map(
                  ({ Name, Id, Status }, i) => (
                    <Chip
                      sx={{
                        height: "auto",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal"
                        }
                      }}
                      key={`category-${Id}`}
                      label={Name}
                      className={`border-radius-5 fsize-14 fweight-400 ${i === 0 ? "" : "mx-2"}  my-2  ${handleCategoriesChipClass(Status)}`}
                    />
                  )
                )
              : "--"}
          </div>
        </div>
      </div>
      <div className="col-md-6 col-12">
        <div className="contact-info-section light-box-shadow border-radius-10 py-4 px-md-5 px-4">
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <h4 className="fsize-17 fweight-600 title-color mb-0">
              {contactInfo}
            </h4>
          </div>
          <div className="row">
            <div className="col-xl-6 col-md-12 mb-4">
              {renderLabelValue(
                exporterName,
                companyContactInfo?.ExporterName || "--",
                <GreyProfileIcon dimensions={"25"} />
              )}
            </div>
            <div className="col-xl-6 col-md-12 mb-4">
              {renderLabelValue(
                mobileNumber,
                companyContactInfo?.MobileNumber || "--",
                <GreyWhatsappIcon dimensions="25" />
              )}
            </div>
            <div className="col-xl-6 col-md-12 mb-4">
              {renderLabelValue(
                emailAddress,
                companyContactInfo?.Email || "--",
                <GreyEnvelopeIcon dimensions="25" />
              )}
            </div>
            <div className="col-xl-6 col-md-12 mb-4">
              {renderLabelValue(
                website,
                companyContactInfo?.Website || "--",
                <GreyGlobeIcon />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompanyInfoSection = () => (
    <div className="company-info-section mt-md-0 mt-4 mb-md-3 mb-4">
      <h4 className="fsize-20 fweight-600 title-color mb-4">
        {companyInfo}
      </h4>
      <div className="row">
        <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
          {renderLabelValue(
            yearEstablished,
            exporterCompanyDetails?.EstablishedYear || "--",
            <GreyCalendarIcon />
          )}
        </div>
        <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
          {renderLabelValue(
            yearsOfExperience,
            exporterCompanyDetails?.YearsOfExperience?.toString() ||
              "--",
            <GreyConvertCubeIcon />
          )}
        </div>
        <div className="col-lg-3 col-md-6  mb-4 mb-md-0">
          {renderLabelValue(
            country,
            exporterCompanyDetails?.CountryName || "--",
            <GreyLocationIcon />
          )}
        </div>
        <div className="col-lg-3 col-md-6">
          {renderLabelValue(
            businessType,
            exporterCompanyDetails?.BusinessTypeName || "--",
            <GreyBriefCaseIcon />
          )}
        </div>
      </div>
    </div>
  );

  const renderShowCasesSection = () => (
    <div className="showcases-section px-md-5 px-md-5 px-3 py-2 mt-md-5 mt-3">
      {exporterCompanyDetails?.ShowCases?.length ? (
        <div className="pt-3">
          <div className="showcase-title-container d-flex justify-content-between align-items-center w-100 py-md-0 py-3">
            <h4 className="fsize-20 fweight-600 title-color mb-0 py-md-2">
              {showcases}
            </h4>
          </div>
          {exporterCompanyDetails?.ShowCases?.map(
            (showcase, index) => {
              const productsCardsArray = fillInProductCards(
                showcase?.Products
              );

              return (
                <div key={`${showcase?.Name}-${index}`}>
                  <div
                    className={`showcase-img-products-container my-5 ${index === 0 && "mt-3"} ${
                      index !==
                        exporterCompanyDetails?.ShowCases.length -
                          1 && "light-border-bottom"
                    }`}>
                    <div className="img-container ">
                      <div
                        className="showcase-image"
                        style={{
                          backgroundImage: `url(${validShowcaseImagesId.includes(showcase?.Id) ? showcase?.Image : noImgPlaceholder})`
                        }}>
                        <div className="showcase-info d-flex justify-content-center align-items-center text-center">
                          <div>
                            <Typography className="fweight-700 fsize-28 px-md-5 px-3">
                              {showcase?.Name}
                            </Typography>
                            <Typography className="px-lg-5 px-3 ">
                              {checkForMultiline(
                                showcase?.Description,
                                "fweight-500 fsize-16",
                                "me-0 ms-0"
                              )}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="products-container d-flex flex-wrap justify-content-end gap-3 ps-xl-5 w-100">
                      {productsCardsArray.map((product) => product)}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      ) : (
        <EmptyState
          subTitle={noShowcasesEmptyPlaceholder}
          imgSrc={noShowcasesImage}
        />
      )}
    </div>
  );

  return (
    <div className="exporter-profile-container">
      <div className="page-title pb-4">
        <Typography
          variant="h5"
          className="title-color fweight-700 fsize-22">
          {pageTitle}
        </Typography>
      </div>
      <div className="bg-white border-radius-10">
        {renderHeaderSection()}
        {renderExporterInfoAndTabsSection()}
      </div>
      {tabValue === 0 ? (
        <div>
          <div className="px-md-5 px-4 py-md-4 py-2 border-radius-10 bg-white">
            {renderDescriptionContactInfoSection()}
            {renderCompanyInfoSection()}
          </div>
          <div className="bg-white my-5 border-radius-10">
            {renderShowCasesSection()}
          </div>
        </div>
      ) : tabValue === 1 ? (
        <div>
          <div className="px-md-5 px-4 py-md-4 py-2 border-radius-10 bg-white">
            {renderAccountInfoSection()}
          </div>
        </div>
      ) : (
        <VerificationAndSubscription />
      )}
    </div>
  );
};

export default ExporterDetails;
